export type PaymentSubscriptionReference = {
  id: string;
  status?: string;
  stripeSubscriptionId?: string;
};

/** Group subscription context for Payment tab actions (Stripe v2 + legacy Braintree id fields). */
export type PaymentTabSubscription = {
  stripeSubscriptionId?: string;
  /** Legacy Braintree subscription id or local fallback. */
  id?: string;
  paymentMethodToken?: string;
};

export type TPayment = {
  id?: string;
  token: string;
  type?: string;
  default?: boolean;
  cardType?: string;
  cardholderName?: string;
  email?: string;
  maskedNumber?: string;
  expirationMonth?: string;
  expirationYear?: string;
  expirationDate?: string;
  createdAt?: string;
  bin?: string;
  subscriptions?: PaymentSubscriptionReference[];
  billing_details?: {
    name?: string | null;
    email?: string | null;
    phone?: string | null;
  };
  card?: {
    brand?: string;
    last4?: string;
    exp_month?: number;
    exp_year?: number;
  };
  [key: string]: any;
};

/** Stripe invoice transaction status stored on UserStripeTransaction (Backend-payment). */
export enum StripeTransactionStatus {
  Paid = 'paid',
  Refunded = 'refunded',
  Draft = 'draft',
  Open = 'open',
  Uncollectible = 'uncollectible',
  Void = 'void',
  Failed = 'failed',
  Unknown = 'unknown',
}

/** UI grouping for billing list filters and badges. */
export enum BillingPaymentStatus {
  Paid = 'paid',
  Pending = 'pending',
  Failed = 'failed',
  Refunded = 'refunded',
  NotPaid = 'not_paid',
}

export type BillingInvoiceDisplayCategory = 'paid' | 'pending' | 'failed' | 'unpaid';

export const BILLING_PAYMENT_STATUS_LABELS: Record<BillingPaymentStatus, string> = {
  [BillingPaymentStatus.Paid]: 'Paid',
  [BillingPaymentStatus.Pending]: 'Pending',
  [BillingPaymentStatus.Failed]: 'Failed',
  [BillingPaymentStatus.Refunded]: 'Refunded',
  [BillingPaymentStatus.NotPaid]: 'Not Paid',
};

export function isStripeTransactionStatus(value: unknown): value is StripeTransactionStatus {
  return typeof value === 'string' && Object.values(StripeTransactionStatus).includes(value as StripeTransactionStatus);
}

export function getStripeTransactionStatusLabel(status: StripeTransactionStatus | string): string {
  if (status === StripeTransactionStatus.Paid) return 'Paid';
  if (status === StripeTransactionStatus.Refunded) return 'Refunded';
  if (status === StripeTransactionStatus.Draft || status === StripeTransactionStatus.Open) return 'Pending';
  if (
    status === StripeTransactionStatus.Uncollectible ||
    status === StripeTransactionStatus.Void ||
    status === StripeTransactionStatus.Failed
  ) {
    return 'Failed';
  }
  return 'Unpaid';
}

export function getStripeTransactionDisplayCategory(status: StripeTransactionStatus | string): BillingInvoiceDisplayCategory {
  if (status === StripeTransactionStatus.Paid) return 'paid';
  if (status === StripeTransactionStatus.Draft || status === StripeTransactionStatus.Open) return 'pending';
  if (
    status === StripeTransactionStatus.Uncollectible ||
    status === StripeTransactionStatus.Void ||
    status === StripeTransactionStatus.Failed ||
    status === StripeTransactionStatus.Refunded
  ) {
    return 'failed';
  }
  return 'unpaid';
}

export function mapStripeTransactionStatusToBillingPaymentStatus(status: StripeTransactionStatus | string): BillingPaymentStatus {
  if (status === StripeTransactionStatus.Paid) return BillingPaymentStatus.Paid;
  if (status === StripeTransactionStatus.Refunded) return BillingPaymentStatus.Refunded;
  if (status === StripeTransactionStatus.Draft || status === StripeTransactionStatus.Open) return BillingPaymentStatus.Pending;
  if (
    status === StripeTransactionStatus.Uncollectible ||
    status === StripeTransactionStatus.Void ||
    status === StripeTransactionStatus.Failed
  ) {
    return BillingPaymentStatus.Failed;
  }
  return BillingPaymentStatus.NotPaid;
}

export function getBillingInvoiceStatusClassName(
  status: StripeTransactionStatus | string,
  css: Record<string, string>,
): string {
  const category = getStripeTransactionDisplayCategory(status);
  if (category === 'paid') return css.statusPaid;
  if (category === 'pending') return css.statusPending;
  if (category === 'failed') return css.statusFailed;
  return css.statusUnpaid;
}
